import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Slider } from '@atlas/components';
import { Logger } from '@atlas/core';
import { ChipsItem, Icon, Label } from '@atlas/ui';

import {
  getSmarticoTournaments,
  useSmartico,
} from 'components/ClientProfile/Achievements/Smartico';
import { calculateTimeLeft } from 'components/Tournaments/Smartico/shares';
import TournamentsCard from 'components/Tournaments/Smartico/TournamentsCard';
import { BASE_PATH } from 'src/common/constants';
import { CDN_TYPE_CMS, imageUrlCDN } from 'src/common/utils';

const LobbyTournamentsBlock = ({ blockTitleKey, imageUrl }) => {
  const [swiper, setSwiper] = useState(null);
  const prevSlide = () => swiper && swiper.slidePrev();
  const nextSlide = () => swiper && swiper.slideNext();
  const [isShowImg, toggleVisibilityImg] = useState(true);
  const handleError = () => toggleVisibilityImg(false);

  const [isLocked, setIsLocked] = useState(false);

  const { push } = useHistory();
  const { isReadySmartico } = useSmartico();
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState(null);
  const [timeLeftForTournaments, setTimeLeftForTournaments] = useState({});

  const tournamentsUpdates = useCallback(tournaments => {
    Logger.debug('Smartico.tournaments', tournaments);
    setTournaments(tournaments?.filter(t => t.is_upcoming || t.is_in_progress) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isReadySmartico) {
      getSmarticoTournaments({
        cb: tournamentsUpdates,
        tournamentsUpdates,
      });
    }
  }, [isReadySmartico, tournamentsUpdates]);

  useEffect(() => {
    const updateTournamentTimes = () => {
      const updatedTimeLeft = {};
      tournaments?.forEach(tournament => {
        const targetTime = tournament.is_in_progress
          ? new Date(tournament.end_time).getTime()
          : new Date(tournament.start_time).getTime();
        updatedTimeLeft[tournament.instance_id] = calculateTimeLeft(targetTime);
      });
      setTimeLeftForTournaments(updatedTimeLeft);
    };

    const interval = setInterval(updateTournamentTimes, 1000);

    return () => clearInterval(interval);
  }, [tournaments]);

  if (loading || tournaments.length === 0) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-tournaments">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={32}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/tournaments.png`
                }
                width={32}
              />
            </div>
          )}
          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
        <div className="casino-lobby__right">
          {!isLocked && (
            <>
              <ChipsItem
                className="casino-lobby__arrow casino-lobby__arrow-left"
                label={<Icon name="arrow-left" />}
                onClick={prevSlide}
              />
              <ChipsItem
                className="casino-lobby__arrow casino-lobby__arrow-right"
                label={<Icon name="arrow-right" />}
                onClick={nextSlide}
              />
            </>
          )}
        </div>
      </div>

      <div className="casino-lobby__content">
        <div className="lobby-tournaments__slider">
          <Slider
            autoplay={{
              delay: 2000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            direction="horizontal"
            loop={false}
            onBeforeInit={setSwiper}
            onLock={() => setIsLocked(true)}
            onUnlock={() => setIsLocked(false)}
            onUpdate={swiperInstance => setIsLocked(swiperInstance.isLocked)}
            spaceBetween={14}
            speed={1000}
          >
            {tournaments.map(tournament => (
              <Slider.Slide key={tournament.instance_id}>
                <TournamentsCard
                  pathname="/tournaments"
                  push={push}
                  timeLeftForTournaments={timeLeftForTournaments}
                  tournament={tournament}
                />
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default LobbyTournamentsBlock;
